import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, BadgeCheck, Phone, CalendarCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, inr, priceLabel } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { PHONE, PHONE_LINK } from "@/lib/brand";

const AVAIL = {
  available: { label: "Available for Bookings", cls: "bg-sage/60 text-ink" },
  unavailable: { label: "Currently Unavailable", cls: "bg-ink/10 text-ink/60" },
  enquiry_required: { label: "Enquiry Required", cls: "bg-blush/60 text-ink" },
};

export default function ArtistProfile() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setArtist(null);
    setNotFound(false);
    api.get(`/artists/${slug}`).then((r) => setArtist(r.data)).catch(() => setNotFound(true));
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get(`/reviews?artist_slug=${slug}`).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug]);

  if (notFound)
    return (
      <div data-testid="artist-not-found" className="px-4 max-w-7xl mx-auto py-24 text-center">
        <h1 className="font-serif text-3xl text-ink">Artist not found</h1>
        <Link to="/makeup-artists" className="mt-4 inline-block text-velvet font-semibold">← Back to artists</Link>
      </div>
    );

  if (!artist)
    return <div data-testid="artist-loading" className="py-24 text-center text-ink/50">Loading profile…</div>;

  const avail = AVAIL[artist.availability_status] || AVAIL.available;
  const artistServices = services.filter((s) => (artist.services || []).includes(s.name));

  return (
    <div data-testid="artist-profile-page">
      <SEO
        title={`${artist.name} — Makeup Artist in ${artist.location}`}
        description={(artist.bio || "").slice(0, 155)}
        path={`/makeup-artists/${slug}`}
      />

      <section className="bg-white border-b border-[#EAE5DF]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-8 sm:py-14">
          <Link to="/makeup-artists" data-testid="back-to-artists" className="inline-flex items-center gap-1 text-sm text-ink/50 hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" /> All artists
          </Link>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8 lg:gap-14">
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
              <img src={artist.photo} alt={artist.name} data-testid="artist-photo"
                className="w-full rounded-[2rem] object-cover object-top aspect-[4/5] shadow-xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              <span data-testid="artist-availability-badge" className={`inline-block text-xs font-semibold px-4 py-1.5 rounded-full ${avail.cls}`}>{avail.label}</span>
              <h1 className="mt-4 font-serif font-bold text-ink text-3xl sm:text-4xl lg:text-5xl">{artist.name}</h1>
              {artist.title && <p className="mt-1 text-xs uppercase tracking-[0.3em] font-semibold text-velvet">{artist.title}</p>}
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink/65">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-velvet" /> {artist.location}</span>
                <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-velvet" /> {artist.experience_years}+ years experience</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-gold fill-gold" /> {artist.rating} rating</span>
                <span className="flex items-center gap-1.5"><CalendarCheck className="w-4 h-4 text-velvet" /> From {inr(artist.starting_price)}</span>
              </div>
              <p className="mt-6 text-ink/70 leading-relaxed max-w-2xl" data-testid="artist-bio">{artist.bio}</p>
              <div className="mt-6">
                <p className="text-xs uppercase tracking-widest font-semibold text-ink/40">Cities Served</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(artist.cities_served || []).map((c) => (
                    <span key={c} className="text-xs bg-cream text-ink/70 px-3 py-1.5 rounded-full">{c}</span>
                  ))}
                </div>
              </div>
              {artist.availability_note && (
                <p className="mt-5 text-sm text-ink/55 italic" data-testid="artist-availability-note">{artist.availability_note}</p>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={`/enquire?artist=${artist.slug}`}>
                  <Button data-testid="artist-book-enquire-btn" className="w-full sm:w-auto rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold px-10 h-12 text-base active:scale-95 transition-transform">
                    Book / Enquire
                  </Button>
                </Link>
                <a href={PHONE_LINK}>
                  <Button data-testid="artist-call-btn" variant="outline" className="w-full sm:w-auto rounded-full border-ink/25 px-10 h-12 text-base">
                    <Phone className="w-4 h-4 mr-2" /> Call {PHONE}
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      {(artist.portfolio || []).length > 0 && (
        <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 sm:py-20" data-testid="portfolio-section">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Portfolio</p>
          <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl">Recent work</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
            {artist.portfolio.map((p, i) => (
              <button key={i} data-testid={`portfolio-item-${i}`} onClick={() => setLightbox(p)}
                className="group relative rounded-2xl overflow-hidden aspect-[3/4] bg-cream">
                <img src={p.url} alt={p.tag || "Portfolio"} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {p.tag && (
                  <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/70 to-transparent text-ivory text-xs p-3 text-left">
                    {p.tag}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* SERVICES & RATES */}
      <section className="bg-white border-y border-[#EAE5DF]" data-testid="artist-rates-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 sm:py-20">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Services & Rates</p>
          <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl">Transparent pricing</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {artistServices.map((s) => (
              <div key={s.id} data-testid={`artist-service-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-ivory rounded-2xl p-5 border border-[#EAE5DF] flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-lg text-ink">{s.name}</h3>
                  {s.travel_charge != null && <p className="text-xs text-ink/50 mt-1">+ {inr(s.travel_charge)} travel</p>}
                </div>
                <span className="text-sm font-semibold text-velvet whitespace-nowrap">{priceLabel(s)}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-ink/50">Final quote depends on event date, location and requirements. Payment is collected personally after booking confirmation.</p>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 sm:py-20" data-testid="artist-reviews-section">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Reviews</p>
          <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl">What clients say</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`artist-review-${r.id}`} className="bg-white rounded-2xl p-6 border border-[#EAE5DF]">
                <div className="flex gap-0.5">
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-gold fill-gold" />
                  ))}
                </div>
                <p className="mt-3 text-sm text-ink/75 leading-relaxed">"{r.text}"</p>
                <p className="mt-4 font-semibold text-sm text-ink">{r.customer_name}</p>
                {r.event_type && <p className="text-xs text-velvet mt-0.5">{r.event_type}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div data-testid="portfolio-lightbox" className="fixed inset-0 z-[100] bg-ink/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox.url} alt={lightbox.tag || "Portfolio"} className="max-h-[85vh] max-w-full rounded-2xl object-contain" />
        </div>
      )}
    </div>
  );
}
